import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const HTMLElements = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">HTML Foundations</div>
                <h1>Elements & Attributes Deep Dive</h1>
                <p className="hero-lead">
                    HTML elements are the building blocks of web pages. Master how elements work, their anatomy,
                    and how attributes provide additional information and functionality.
                </p>
            </div>

            <div className="content-section">
                <h2>Anatomy of an HTML Element</h2>
                <p>
                    An HTML element typically consists of three parts: an opening tag, content, and a closing tag.
                </p>

                <div className="diagram-box">
                    <CodeBlock language="html" code={`<tagname attribute="value">Content goes here</tagname>
                                               
Opening  Attribute  Value      Content        Closing
  Tag                                           Tag`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Complete Element Example</span>
                    </div>
                    <CodeBlock language="html" code={`<p class="intro" id="first-paragraph">
    This is a paragraph with attributes.
</p>`} />
                </div>

                <div className="explanation-list">
                    <h3>Breaking it down:</h3>
                    <ul>
                        <li><code>&lt;p&gt;</code> - Opening tag (defines a paragraph)</li>
                        <li><code>class="intro"</code> - Attribute for CSS styling</li>
                        <li><code>id="first-paragraph"</code> - Unique identifier for the element</li>
                        <li><code>This is a paragraph...</code> - The content</li>
                        <li><code>&lt;/p&gt;</code> - Closing tag (ends the paragraph)</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Block vs. Inline Elements</h2>
                <p>
                    HTML elements are divided into two main display categories: block-level and inline elements.
                </p>

                <div className="comparison-table">
                    <h3>Key Differences:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Block Elements</th>
                                <th>Inline Elements</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Start on a new line</td>
                                <td>Flow within text, don't break lines</td>
                            </tr>
                            <tr>
                                <td>Take up full width available</td>
                                <td>Only take up necessary width</td>
                            </tr>
                            <tr>
                                <td>Can contain block and inline elements</td>
                                <td>Can only contain inline elements and text</td>
                            </tr>
                            <tr>
                                <td>Examples: <code>&lt;div&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;h1&gt;</code></td>
                                <td>Examples: <code>&lt;span&gt;</code>, <code>&lt;a&gt;</code>, <code>&lt;strong&gt;</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Block vs. Inline Example</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Block elements (each on new line) --&gt;
<h1>This is a heading</h1>
<p>This is a paragraph</p>
<div>This is a div container</div>

<!-- Inline elements (within text flow) --&gt;
<p>This paragraph contains <strong>bold text</strong> and <em>italic text</em> inline.</p>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Block-Level Elements</h2>

                <div className="element-showcase">
                    <div className="element-card">
                        <h3><code>&lt;div&gt;</code> - Division/Container</h3>
                        <p><strong>Purpose:</strong> Generic container for grouping content</p>
                        <p><strong>Use case:</strong> Layout sections, applying styles to groups of elements</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<div class="header">
    <h1>Welcome</h1>
    <p>Subtitle text</p>
</div>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;p&gt;</code> - Paragraph</h3>
                        <p><strong>Purpose:</strong> Text paragraphs</p>
                        <p><strong>Use case:</strong> Body text, article content</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>This is a paragraph of text. Browsers automatically add space before and after.</p>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;h1&gt;</code> to <code>&lt;h6&gt;</code> - Headings</h3>
                        <p><strong>Purpose:</strong> Section headings (hierarchical)</p>
                        <p><strong>Use case:</strong> Document structure, SEO</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<h1>Main Title</h1>
<h2>Section Heading</h2>
<h3>Subsection</h3>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;ul&gt;</code>, <code>&lt;ol&gt;</code>, <code>&lt;li&gt;</code> - Lists</h3>
                        <p><strong>Purpose:</strong> Unordered and ordered lists</p>
                        <p><strong>Use case:</strong> Navigation menus, feature lists, steps</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<ul>
    <li>Item 1</li>
    <li>Item 2</li>
</ul>

<ol>
    <li>First step</li>
    <li>Second step</li>
</ol>`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Common Inline Elements</h2>

                <div className="element-showcase">
                    <div className="element-card">
                        <h3><code>&lt;span&gt;</code> - Inline Container</h3>
                        <p><strong>Purpose:</strong> Generic inline container</p>
                        <p><strong>Use case:</strong> Styling specific text portions</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>The price is <span class="highlight">$99.99</span> today!</p>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;a&gt;</code> - Anchor/Link</h3>
                        <p><strong>Purpose:</strong> Hyperlinks to other pages or sections</p>
                        <p><strong>Use case:</strong> Navigation, external links</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<a href="https://example.com">Visit Example</a>
<a href="#section2">Jump to Section 2</a>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;strong&gt;</code> and <code>&lt;em&gt;</code> - Emphasis</h3>
                        <p><strong>Purpose:</strong> Semantic emphasis (strong importance, emphasis)</p>
                        <p><strong>Use case:</strong> Important text, stressed words</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p><strong>Warning:</strong> This action cannot be undone.</p>
<p>I <em>really</em> need to finish this.</p>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;img&gt;</code> - Image</h3>
                        <p><strong>Purpose:</strong> Embed images</p>
                        <p><strong>Use case:</strong> Photos, illustrations, icons</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<img src="logo.png" alt="Company Logo" width="200" height="100">`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Void (Self-Closing) Elements</h2>
                <p>
                    Some elements don't have content and therefore don't need a closing tag. These are called <strong>void elements</strong> or <strong>empty elements</strong>.
                </p>

                <div className="element-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Element</th>
                                <th>Purpose</th>
                                <th>Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>&lt;br&gt;</code></td>
                                <td>Line break</td>
                                <td><code>Line 1&lt;br&gt;Line 2</code></td>
                            </tr>
                            <tr>
                                <td><code>&lt;hr&gt;</code></td>
                                <td>Horizontal rule (divider)</td>
                                <td><code>&lt;hr&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>&lt;img&gt;</code></td>
                                <td>Image</td>
                                <td><code>&lt;img src="pic.jpg" alt="Photo"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>&lt;input&gt;</code></td>
                                <td>Form input field</td>
                                <td><code>&lt;input type="text" name="username"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>&lt;meta&gt;</code></td>
                                <td>Metadata</td>
                                <td><code>&lt;meta charset="UTF-8"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>&lt;link&gt;</code></td>
                                <td>External resource link</td>
                                <td><code>&lt;link rel="stylesheet" href="style.css"&gt;</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>HTML5 Note:</strong> In HTML5, the trailing slash (<code>/&gt;</code>) is optional for void elements.
                        Both <code>&lt;br&gt;</code> and <code>&lt;br /&gt;</code> are valid.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>HTML Attributes Explained</h2>
                <p>
                    Attributes provide additional information about HTML elements. They appear in the opening tag and follow a <code>name="value"</code> format.
                </p>

                <div className="attribute-syntax">
                    <h3>Attribute Syntax Rules:</h3>
                    <ul>
                        <li>Attributes go in the <strong>opening tag</strong> only</li>
                        <li>Format: <code>attribute-name="value"</code></li>
                        <li>Use quotes (double or single) around values</li>
                        <li>Multiple attributes separated by spaces</li>
                        <li>Attribute names are case-insensitive (but lowercase is conventional)</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Multiple Attributes Example</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="photo.jpg" alt="Sunset" width="800" height="600" class="featured">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Universal (Global) Attributes</h2>
                <p>
                    These attributes can be used on <em>any</em> HTML element:
                </p>

                <div className="attributes-grid">
                    <div className="attribute-card">
                        <h3><code>id</code></h3>
                        <p><strong>Purpose:</strong> Unique identifier for an element</p>
                        <p><strong>Rules:</strong> Must be unique on the page, used for CSS/JS targeting</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<div id="header">...</div>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>class</code></h3>
                        <p><strong>Purpose:</strong> One or more class names for CSS styling</p>
                        <p><strong>Rules:</strong> Can repeat across elements, space-separated for multiple classes</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p class="intro highlight">Text</p>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>style</code></h3>
                        <p><strong>Purpose:</strong> Inline CSS styles</p>
                        <p><strong>Warning:</strong> Avoid for maintainabilityuse external CSS instead</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p style="color: red; font-size: 18px;">Styled text</p>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>title</code></h3>
                        <p><strong>Purpose:</strong> Tooltip text that appears on hover</p>
                        <p><strong>Use case:</strong> Additional information, abbreviation expansions</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<abbr title="HyperText Markup Language">HTML</abbr>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>data-*</code></h3>
                        <p><strong>Purpose:</strong> Custom data attributes for JavaScript</p>
                        <p><strong>Use case:</strong> Store extra information without affecting display</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<button data-user-id="123" data-action="delete">Delete</button>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>lang</code></h3>
                        <p><strong>Purpose:</strong> Language of element content</p>
                        <p><strong>Use case:</strong> Accessibility, translation tools, SEO</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p lang="es">Hola mundo</p>`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Element-Specific Attributes</h2>
                <p>
                    Some attributes are specific to certain elements:
                </p>

                <div className="specific-attributes">
                    <h3><code>&lt;a&gt;</code> (Link) Attributes:</h3>
                    <ul>
                        <li><code>href</code> - Destination URL</li>
                        <li><code>target</code> - Where to open link (<code>_blank</code>, <code>_self</code>, etc.)</li>
                        <li><code>rel</code> - Relationship to target (<code>nofollow</code>, <code>noopener</code>)</li>
                    </ul>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<a href="https://example.com" target="_blank" rel="noopener">External Link</a>`} />
                    </div>

                    <h3><code>&lt;img&gt;</code> (Image) Attributes:</h3>
                    <ul>
                        <li><code>src</code> - Image file path (required)</li>
                        <li><code>alt</code> - Alternative text for accessibility (required)</li>
                        <li><code>width</code> and <code>height</code> - Dimensions (optional but recommended)</li>
                    </ul>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<img src="logo.png" alt="Company Logo" width="200" height="100">`} />
                    </div>

                    <h3><code>&lt;input&gt;</code> (Form Input) Attributes:</h3>
                    <ul>
                        <li><code>type</code> - Input type (text, email, password, etc.)</li>
                        <li><code>name</code> - Name for form submission</li>
                        <li><code>value</code> - Default/current value</li>
                        <li><code>placeholder</code> - Hint text</li>
                        <li><code>required</code> - Makes field mandatory</li>
                    </ul>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<input type="email" name="user_email" placeholder="Enter email" required>`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Boolean Attributes</h2>
                <p>
                    Some attributes are boolean (true/false). Their presence = true, absence = false.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Boolean Attributes</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- These are equivalent: --&gt;
<input type="checkbox" checked>
<input type="checkbox" checked="checked">
<input type="checkbox" checked="">

<!-- Disabled button --&gt;
<button disabled>Can't Click Me</button>

<!-- Required field --&gt;
<input type="text" required>

<!-- Readonly field --&gt;
<input type="text" value="Cannot edit" readonly>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use semantic elements (<code>&lt;header&gt;</code>, <code>&lt;nav&gt;</code>, <code>&lt;article&gt;</code>)</li>
                            <li>Always include <code>alt</code> text for images</li>
                            <li>Use <code>id</code> for unique elements, <code>class</code> for reusable styles</li>
                            <li>Nest elements properly (close in reverse order)</li>
                            <li>Use lowercase for element and attribute names</li>
                            <li>Quote all attribute values</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use <code>&lt;div&gt;</code> for everything (lack of semantics)</li>
                            <li>Forget closing tags (except void elements)</li>
                            <li>Overlap tags incorrectly</li>
                            <li>Use inline styles excessively</li>
                            <li>Duplicate <code>id</code> values on same page</li>
                            <li>Use deprecated elements (<code>&lt;font&gt;</code>, <code>&lt;center&gt;</code>)</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLElements;


